/**
 * <s-search-suggest> — matching-parts dropdown for the always-visible header search bar.
 *
 * Place it inside a header search <form> (which must be position: relative). As the
 * shopper types 2+ characters it fetches Shopify predictive search results and shows
 * up to 6 matching products under the bar, before they press Search. Pressing Search
 * or Enter (with no highlighted suggestion) still submits the normal search.
 *
 * Data attributes (written by Liquid):
 *   data-url         routes.predictive_search_url
 *   data-search-url  routes.search_url
 *   data-currency    ISO currency code for price formatting
 *   data-label       accessible name for the list
 *   data-view-all    "See all results" text containing [terms]
 *   data-empty       text shown when nothing matches
 */
class SearchSuggest extends HTMLElement {
  #ac;
  #input;
  #form;
  #timer;
  #request;
  #active = -1;
  #lastTerms = '';

  connectedCallback() {
    this.#form = this.closest('form');
    this.#input = this.#form?.querySelector('input[type="search"]');
    if (!this.#input) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const listId = `${this.#input.id || 'header-search'}-suggestions`;

    this.hidden = true;
    this.id = listId;
    this.#input.setAttribute('role', 'combobox');
    this.#input.setAttribute('aria-autocomplete', 'list');
    this.#input.setAttribute('aria-controls', listId);
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.setAttribute('autocomplete', 'off');

    this.#input.addEventListener('input', () => this.#queue(), opts);
    this.#input.addEventListener('focus', () => { if (this.#lastTerms && this.childElementCount) this.#open(); }, opts);
    this.#input.addEventListener('keydown', (e) => this.#onKey(e), opts);
    document.addEventListener('pointerdown', (e) => { if (!this.#form.contains(e.target)) this.#close(); }, opts);
    this.#form.addEventListener('focusout', (e) => { if (!this.#form.contains(e.relatedTarget)) this.#close(); }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  #queue() {
    clearTimeout(this.#timer);
    const terms = this.#input.value.trim();
    if (terms.length < 2) {
      this.#lastTerms = '';
      this.replaceChildren();
      this.#close();
      return;
    }
    this.#timer = setTimeout(() => this.#fetch(terms), 200);
  }

  async #fetch(terms) {
    this.#request?.abort();
    this.#request = new AbortController();
    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const res = await fetch(`${this.dataset.url}.json?${params}`, { signal: this.#request.signal });
      if (!res.ok) return;
      const data = await res.json();
      if (terms !== this.#input.value.trim()) return;
      this.#lastTerms = terms;
      this.#render(terms, data?.resources?.results?.products || []);
    } catch (err) {
      if (err.name !== 'AbortError') this.#close();
    }
  }

  #price(value) {
    const n = Number(value);
    if (!value || Number.isNaN(n)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: this.dataset.currency || 'USD' }).format(n);
    } catch {
      return String(value);
    }
  }

  #render(terms, products) {
    this.#active = -1;
    this.#input.removeAttribute('aria-activedescendant');
    const list = document.createElement('ul');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', this.dataset.label || '');
    list.className = 'max-h-82 overflow-y-auto';

    products.forEach((p, i) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.id = `${this.id}-${i}`;
      const a = document.createElement('a');
      a.href = p.url;
      a.tabIndex = -1;
      a.className = 'flex items-center gap-3 px-3 py-2 hover:bg-surface-secondary';
      if (p.image) {
        const img = document.createElement('img');
        img.src = p.image.includes('?') ? `${p.image}&width=96` : `${p.image}?width=96`;
        img.alt = '';
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        img.className = 'size-11 shrink-0 rounded object-contain bg-surface-secondary';
        a.append(img);
      }
      const text = document.createElement('span');
      text.className = 'min-w-0 flex-1';
      const title = document.createElement('span');
      title.className = 'block text-sm font-semibold line-clamp-2';
      title.textContent = p.title;
      text.append(title);
      const price = this.#price(p.price);
      if (price) {
        const pr = document.createElement('span');
        pr.className = 'block text-xs text-on-surface-secondary';
        pr.textContent = price;
        text.append(pr);
      }
      a.append(text);
      li.append(a);
      list.append(li);
    });

    const footer = document.createElement('a');
    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('type', 'product');
    footer.href = url.pathname + url.search;
    footer.tabIndex = -1;
    footer.className = 'block border-t border-border px-3 py-2 text-sm font-bold text-accent hover:bg-surface-secondary';
    footer.textContent = products.length
      ? (this.dataset.viewAll || '').replace('[terms]', terms)
      : (this.dataset.empty || '');

    this.replaceChildren(...(products.length ? [list] : []), footer);
    this.#open();
  }

  get #options() {
    return [...this.querySelectorAll('[role="option"]')];
  }

  #setActive(index) {
    const options = this.#options;
    if (!options.length) return;
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    options.forEach((o) => {
      o.setAttribute('aria-selected', 'false');
      o.firstElementChild?.classList.remove('bg-surface-secondary');
    });
    const opt = options[index];
    opt.setAttribute('aria-selected', 'true');
    opt.firstElementChild?.classList.add('bg-surface-secondary');
    opt.scrollIntoView({ block: 'nearest' });
    this.#input.setAttribute('aria-activedescendant', opt.id);
    this.#active = index;
  }

  #onKey(e) {
    if (this.hidden) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.#setActive(this.#active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.#setActive(this.#active - 1); }
    else if (e.key === 'Escape') { this.#close(); }
    else if (e.key === 'Enter' && this.#active > -1) {
      const link = this.#options[this.#active]?.querySelector('a');
      if (link) { e.preventDefault(); window.location.href = link.href; }
    }
  }

  #open() {
    this.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.hidden = true;
    this.#active = -1;
    this.#input?.setAttribute('aria-expanded', 'false');
    this.#input?.removeAttribute('aria-activedescendant');
  }
}

if (!customElements.get('s-search-suggest')) customElements.define('s-search-suggest', SearchSuggest);
